import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

export default function PeekInside({ book, onClose }) {
  const [page, setPage] = useState(0);
  const pages = book.preview;
  const last = pages.length - 1;

  useEffect(() => {
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="peek-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, scale: 0.95, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 40, scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl overflow-hidden rounded-3xl border-2 border-ink bg-cream shadow-pop-lg"
      >
        <div className="flex items-center justify-between border-b-2 border-ink px-5 py-3.5">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-ink/60">
            Sneak peek · {book.title}: {book.subtitle}
          </p>
          <button
            data-testid="peek-close-btn"
            onClick={onClose}
            aria-label="Close preview"
            className="rounded-lg border-2 border-ink bg-white p-1.5 shadow-pop-sm transition-transform duration-200 hover:-rotate-6"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="halftone relative flex min-h-72 items-center justify-center border-b-2 border-ink p-6 md:min-h-[26rem] md:border-b-0 md:border-r-2"
            style={{ background: `radial-gradient(circle at 50% 35%, ${book.color}22, transparent 70%), #FFFDF6` }}
          >
            <img
              src={book.cover}
              alt={`${book.title} ${book.subtitle} cover`}
              className="max-h-60 w-auto rounded-lg border-2 border-ink object-contain shadow-pop md:max-h-80"
            />
          </div>

          <div className="relative flex min-h-72 flex-col justify-between p-7 sm:p-9 md:min-h-[26rem]">
            <AnimatePresence mode="wait">
              <motion.div
                key={page}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-ink/40">
                  Sample page {page + 1}
                </p>
                <h3 className="mt-3 font-display text-2xl font-bold" style={{ color: book.color }}>
                  {pages[page].heading}
                </h3>
                <p className="mt-4 font-body text-lg leading-relaxed text-ink/80">{pages[page].text}</p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex gap-2">
                <button
                  data-testid="peek-prev-btn"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  aria-label="Previous page"
                  className="rounded-full border-2 border-ink bg-white p-2.5 shadow-pop-sm transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  data-testid="peek-next-btn"
                  onClick={() => setPage(Math.min(last, page + 1))}
                  disabled={page === last}
                  aria-label="Next page"
                  className="rounded-full border-2 border-ink bg-white p-2.5 shadow-pop-sm transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-30"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <span className="ml-2 self-center font-mono text-xs font-bold uppercase tracking-widest text-ink/50">
                  {page + 1} / {pages.length}
                </span>
              </div>
              <a
                data-testid="peek-amazon-btn"
                href={book.amazon}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-full border-2 border-ink bg-banana px-4 py-2 font-head text-xs font-extrabold uppercase tracking-wide shadow-pop-sm transition-all duration-200 hover:-translate-y-0.5"
              >
                Get the book <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>

        <p className="border-t-2 border-ink bg-banana/40 px-5 py-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-ink/60">
          Teaser preview — full story inside the book
        </p>
      </motion.div>
    </motion.div>
  );
}
